import { 
  RefreshCcw, Search, 
  Loader2, 
  Send,
  CheckCheck,
  FileSpreadsheet,
  Filter,
  ScanLine,
  X,
} from 'lucide-react';
import { React, useState, useEffect, useRef } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import axios from "axios";
import Select, { components } from "react-select";
import SelectDate from "../cors/SelectDate";
import { toast } from "sonner";
import { useAuth } from "../context/AuthProvider";
import { useLanguage } from "../context/LanguageProvider";
import { useTheme } from "../context/ThemeProvider";
import { BASE_URL } from "../apiConfig";
import Loading from '../cors/Loading';
import ImportOrdersGoogleSheets from './ImportOrdersGoogleSheets';
import irsaliyat from '../images/irsaliyat.png'
import turboliv from '../images/turboliv.png'
import ozon from '../images/ozon.png'
import tawsilex from '../images/tawsilex.png'
import sift from '../images/sift.ma.png'
import Nexship from '../images/nexship.png'

export default function FilterOrders({
    role,
    token,
    status,
    loading,
    setLoading,
    searchTerm,
    setSearchTerm,
    fetchOrders,
    setDateRange,
    selectedOrders,
    handleSendOrders,
    sendingOrders,
    handleConfirmOrders,
    confirmingOrders,
    selectedSeller,
    setSelectedSeller,
    selectedCompany,
    setSelectedCompany,
    setShowLivreurModal,
    pagination,
    selectedLivreur,
    setSelectedLivreur,
    selectedProduct,
    setSelectedProduct,
    selectedStatusFilters,
    setSelectedStatusFilters,
  }) {

  const { t, locale } = useLanguage();
  const { isDark } = useTheme();
  const [options, setOptions] = useState([{ value: '', label: t('filter_orders.all_sellers') }]);
  const [livreurOptions, setLivreurOptions] = useState([{ value: '', label: t('filter_orders.all_livreurs') }]);
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [loadingLivreurs, setLoadingLivreurs] = useState(false);
  const [productOptions, setProductOptions] = useState([{ value: '', label: t('filter_orders.all_products') }]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [searchDebounce, setSearchDebounce] = useState(null);
  const [currentDateRange, setCurrentDateRange] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showScannerModal, setShowScannerModal] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [scannerAttempt, setScannerAttempt] = useState(0);
  const scannerRef = useRef(null);
  const { roles } = useAuth();

  const ORDER_STATUS_FILTER_OPTIONS = [
    { value: "nouveau", label: t("status.nouveau") },
    { value: "livre", label: t("status.livre") },
    { value: "Livre(paye)", label: t("status.Livre(paye)") },
    { value: "pending_payment", label: t("status.pending_payment") },
    { value: "confirme", label: t("status.confirme") },
    { value: "reporte", label: t("status.reporte") },
    { value: "Attente De Ramassage", label: t("status.Attente De Ramassage") },
    { value: "Pret a ramasser", label: t("status.pre_a_ramasse") },
    { value: "indelivery", label: t("status.indelivery") },
    { value: "paid", label: t("status.paid") },
    { value: "retour", label: t("status.retour") },
    { value: "Retour client reçu", label: t("status.retour_recu") },
    { value: "refuse", label: t("status.refuse") },
    { value: "annule", label: t("status.annule") },
  ];

  const CustomPlaceholder = (props) => {
    return (
      <components.Placeholder {...props}>
        <div className="flex items-center gap-1">
          <Filter size={12} />
          <span>{t('dashboard.filter_by_status')}</span>
        </div>
      </components.Placeholder>
    );
  };

  const getCompanyLogo = (name) => {
    switch (name.toLowerCase()) {
      case 'irsaliyat':
        return irsaliyat
      case 'turboliv':
        return turboliv
      case 'ozon':
        return ozon
      case 'tawsilex':
        return tawsilex
      case 'sift':
        return sift
      case 'nexship':
        return Nexship
      case 'nexship-v2':
        return Nexship
      default:
        return null
    }
  }
  
  useEffect(() => {
    if (showCompanyModal === true) {
      const fetchCompanies = async () => {
        try {
          const res = await axios.get(
            `${BASE_URL}/api/delivery-companies`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
  
          // keep only active companies
          const activeCompanies = res.data.data
            .filter(company => company.status === "true")
            .map(company => ({
              id: company.id,
              name: company.name,
              logo: getCompanyLogo(company.name),
            }));
  
          setCompanies(activeCompanies);
        } catch (err) {
          toast.error(t('filter_orders.error_loading_sellers'));
        }
      };
  
      fetchCompanies();
    }
  }, [showCompanyModal]);
  

  const fetchSellers = async () => {
    try {
      setLoadingSellers(true);
      const res = await axios.get(`${BASE_URL}/api/search-saller`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      const sellers = res.data.data || [];
      const sellerOptions = [
        { value: '', label: t('filter_orders.all_sellers') },
        ...sellers.map((s) => ({
          value: s.id,
          label: `${s.name ?? t('filter_orders.unknown')} (${s.name_store ?? t('filter_orders.no_store')}) - ${s.status}`,
        }))
      ];

      setOptions(sellerOptions);
      
      if (!selectedSeller) {
        setSelectedSeller(sellerOptions[0]);
      }
    } catch (err) {
      console.error("Error fetching sellers:", err);
      toast.error(t('filter_orders.error_loading_sellers'));
      setOptions([{ value: '', label: t('filter_orders.all_sellers') }]);
    } finally {
      setLoadingSellers(false);
    }
  };

  const fetchLivreurs = async () => {
    try {
      setLoadingLivreurs(true);
      const res = await axios.get(`${BASE_URL}/api/livreurs`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      const livreursData = res.data || [];
      const optionsMapped = [
        { value: '', label: t('filter_orders.all_livreurs') },
        ...livreursData.map((l) => ({
          value: l.id,
          label: `${l.name ?? t('filter_orders.unknown')} (${l.phone ?? ""})`,
        }))
      ];

      setLivreurOptions(optionsMapped);
      
      if (!selectedLivreur) {
        setSelectedLivreur(optionsMapped[0]);
      }
    } catch (err) {
      console.error("Error fetching livreurs:", err);
      toast.error(t('filter_orders.error_loading_livreurs'));
      setLivreurOptions([{ value: '', label: t('filter_orders.all_livreurs') }]);
    } finally {
      setLoadingLivreurs(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoadingProducts(true);
      const res = await axios.get(`${BASE_URL}/api/products`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      const products = res.data.data || [];
      const mappedOptions = [
        { value: '', label: t('filter_orders.all_products') },
        ...products.map((p) => ({
          value: p.id,
          label: p.name,
        }))
      ];

      setProductOptions(mappedOptions);
      
      if (!selectedProduct) {
        setSelectedProduct(mappedOptions[0]);
      }
    } catch (err) {
      console.error("Error fetching products:", err);
      toast.error(t('filter_orders.error_loading_products'));
      setProductOptions([{ value: '', label: t('filter_orders.all_products') }]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleDateRangeChange = (range) => {
    console.log('Date range from SelectDate:', range);
    
    setCurrentDateRange(range);
    setDateRange(range);
    setLoading(true);
    
    fetchOrders(1, searchTerm, range, selectedSeller?.value, selectedLivreur?.value, selectedStatusFilters);
  };

  const handleSellerChange = (selectedOption) => {
    setSelectedSeller(selectedOption);
    setLoading(true);
    const sellerId = selectedOption?.value || null;
    
    fetchOrders(1, searchTerm, currentDateRange, sellerId, selectedLivreur?.value, selectedStatusFilters);
  };

  const handleLivreurChange = (selectedOption) => {
    setSelectedLivreur(selectedOption);
    setLoading(true);
    const livreurId = selectedOption?.value || null;
    
    fetchOrders(1, searchTerm, currentDateRange, selectedSeller?.value, livreurId, selectedStatusFilters);
  };

  const handleProductChange = (selectedOption) => {
    setSelectedProduct(selectedOption);
    setLoading(true);
    const productId = selectedOption?.value || null;
    
    fetchOrders(1, searchTerm, currentDateRange, selectedSeller?.value, selectedLivreur?.value, selectedStatusFilters, undefined, productId);
  };

  const handleStatusFiltersChange = (selectedOptions) => {
    const keys = (selectedOptions || []).map((o) => o.value);
    setSelectedStatusFilters(keys);
    setLoading(true);
    fetchOrders(1, searchTerm, currentDateRange, selectedSeller?.value, selectedLivreur?.value, keys);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    if (searchDebounce) {
      clearTimeout(searchDebounce);
    }
    
    const timeout = setTimeout(() => {
      setLoading(true);
      fetchOrders(1, value, currentDateRange, selectedSeller?.value, selectedLivreur?.value, selectedStatusFilters);
    }, 500);
    
    setSearchDebounce(timeout);
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchOrders(pagination?.currentPage || 1, searchTerm, currentDateRange, selectedSeller?.value, selectedLivreur?.value, selectedStatusFilters);
  };

  const handleSendClick = () => {
    if (selectedOrders.length === 0) {
      toast.warning(t('orders.no_order_selected'));
      return;
    }
    setShowCompanyModal(true);
  };

  const handleConfirmSend = () => {
    if (!selectedCompany) {
      toast.warning(t('filter_orders.select_company'));
      return;
    }
    setShowCompanyModal(false);
    handleSendOrders();
  };

  const openScanner = () => {
    setCameraError(null);
    setScannerAttempt((a) => a + 1);
    setShowScannerModal(true);
  };

  const closeScanner = () => {
    setShowScannerModal(false);
  };

  useEffect(() => {
    if (!showScannerModal) return;

    let scanner = null;

    const startScanner = async () => {
      setCameraError(null);
      scanner = new Html5Qrcode("qr-reader", {
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
        useBarCodeDetectorIfSupported: false,
      });
      scannerRef.current = scanner;

      const onScanSuccess = async (decodedText) => {
        try {
          await scanner.stop();
          scanner.clear();
        } catch (e) {
          // scanner already stopped
        }
        setShowScannerModal(false);
        setSearchTerm(decodedText);
        setLoading(true);
        fetchOrders(1, decodedText, currentDateRange, selectedSeller?.value, selectedLivreur?.value, selectedStatusFilters);
        toast.success(t('filter_orders.scan_code_found', { code: decodedText }));
      };

      // Prefer enumerating cameras (more reliable than facingMode constraint)
      let cameraId = null;
      try {
        const cameras = await Html5Qrcode.getCameras();
        if (cameras && cameras.length > 0) {
          const backCamera = cameras.find((c) => /back|arri|traseira|posterior/i.test(c.label));
          cameraId = (backCamera || cameras[0]).id;
        }
      } catch (err) {
        console.warn("getCameras failed, falling back to facingMode:", err);
        const getCamerasMsg = String(err || "");
        if (/secure|https|localhost/i.test(getCamerasMsg)) {
          setCameraError(t('filter_orders.scan_camera_https'));
          return;
        }
      }

      try {
        await scanner.start(
          cameraId || { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          onScanSuccess,
          () => {
            // ignore individual frame errors while scanning
          }
        );
      } catch (err) {
        console.error("Camera start error:", err);
        const msg = String(err?.message || err || "");
        if (/notallowed|permission|denied/i.test(msg)) {
          setCameraError(t('filter_orders.scan_camera_permission'));
        } else if (/secure|security|insecure|https/i.test(msg)) {
          setCameraError(t('filter_orders.scan_camera_https'));
        } else {
          setCameraError(t('filter_orders.scan_camera_error'));
        }
      }
    };

    startScanner();

    return () => {
      if (scanner) {
        try {
          scanner.stop()
            .then(() => scanner.clear())
            .catch(() => {});
        } catch (e) {
          // scanner not running
        }
        scannerRef.current = null;
      }
    };
  }, [showScannerModal, scannerAttempt]);

  // Shared react-select styles factory so control/menu always fill their
  // parent (parent width is controlled via Tailwind classes per-breakpoint).
  const getSelectStyles = (extra = {}) => ({
    control: (provided, state) => ({
      ...provided,
      cursor: "pointer",
      border: "none",
      borderRadius: "20px",
      width: "100%",
      minHeight: "32px",
      height: "32px",
      outline: "none",
      boxShadow: "none",
      backgroundColor: isDark ? "#292524" : "#fff",
      "&:hover": {
        border: "none",
        boxShadow: "none",
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: isDark ? "#292524" : "#fff",
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 8px 24px rgba(0,0,0,.15)",
      zIndex: 30,
    }),
    menuList: (provided) => ({
      ...provided,
      padding: 4,
    }),
    option: (provided, state) => ({
      ...provided,
      cursor: "pointer",
      backgroundColor: state.isSelected
        ? "var(--color-primary)"
        : state.isFocused
        ? isDark
          ? "#44403c"
          : "#f3f4f6"
        : "transparent",
      color: state.isSelected ? isDark ? "#fff" : "#000" : isDark ? "#fff" : "#000",
      ":active": {
        backgroundColor: "var(--color-primary)",
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: isDark ? "#808080" : "#000000",
    }),
    input: (provided) => ({
      ...provided,
      color: isDark ? "#808080" : "#000000",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: isDark ? "#a8a29e" : "#6b7280",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: isDark ? "#d6d3d1" : "#6b7280",
      padding: 4,
      "&:hover": {
        color: "var(--color-primary)",
      },
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: isDark ? "#d6d3d1" : "#6b7280",
      padding: 4,
      "&:hover": {
        color: "#ef4444",
      },
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0 13px",
    }),
    ...extra,
  });

  return (
    // <div className={`${status === "confirme" && (role === 1 || (role === 2 && roles.send_delivery === 1)) ? "justify-end" : status === "tout" ||status === "nouveau" ? "justify-end" : "justify-center"} flex flex-col md:flex-row md:flex-wrap md:items-center gap-y-2 w-full`}>
    <div className={`justify-end flex flex-col md:flex-row md:flex-wrap md:items-center gap-y-2 w-full`}>
      <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 w-full md:w-auto md:justify-end">
        {/* Search Input */}
        <div className="relative w-full sm:w-44">
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={t('forms.placeholders.search_orders')}
            className="pl-8 pr-4 py-2 border border-gray-200 dark:text-[#808080] dark:placeholder:text-[#808080] text-[11px] rounded-xl bg-white focus:ring-2 focus:ring-[var(--color-primary-300)] focus:border-transparent outline-none transition-all duration-200 w-full"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
        </div>

        {status === "tout" && (
          <div className="w-full sm:w-44">
            <Select
              instanceId="orders-status-filter"
              isMulti
              options={ORDER_STATUS_FILTER_OPTIONS}
              value={ORDER_STATUS_FILTER_OPTIONS.filter((o) =>
                (selectedStatusFilters || []).includes(o.value)
              )}
              onChange={handleStatusFiltersChange}
              components={{
                Placeholder: CustomPlaceholder,
              }}
              className="text-[11px] rounded-xl shadow-sm outline-none border border-gray-200 w-full"
              classNamePrefix="status-filter"
              closeMenuOnSelect={false}
              styles={{
                control: (provided) => ({
                  ...provided,
                  cursor: 'pointer',
                  border: 'none',
                  borderRadius: '20px',
                  width: '100%',
                  minHeight: '32px',
                  outline: 'none',
                  backgroundColor: isDark ? "#292524" : "#fff", // stone-800
                  color: isDark ? "#808080" : "#000000",
                  '&:hover': {
                    border: 'none',
                    boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
                  },
                }),
                multiValueRemove: (provided) => ({
                  ...provided,
                  borderRadius: "999px",
                  backgroundColor: "transparent",
                  color: "#8e8e8eff",
                  cursor: "pointer",
                  transition: "all 0.2s ease",

                  ":hover": {
                    color: "#ef4444",
                  },
                }),
                option: (provided, state) => ({
                    ...provided,
                    cursor: "pointer",
                    backgroundColor: state.isFocused
                      ? isDark
                        ? "#44403c"
                        : "#f3f4f6"
                      : isDark
                      ? "#292524"
                      : "#fff",
                    color: isDark ? "#808080" : "#000000",
                  }),
                dropdownIndicator: (provided) => ({ ...provided, padding: 4 }),
                clearIndicator: (provided) => ({ ...provided, padding: 4 }),
                valueContainer: (provided) => ({ ...provided, padding: '2px 10px' }),
                multiValue: (provided) => ({ ...provided, fontSize: '11px' }),
                multiValueLabel: (provided) => ({ ...provided, padding: '2px 6px' }),
              }}
            />
          </div>
        )}

        <div className="flex flex-wrap gap-2 w-full sm:w-auto sm:justify-end">
          {status === "nouveau" && (role === 1 || role === 2 || role === 0) && (
            <button
              onClick={() => setShowImportModal(true)}
              disabled={loading || sendingOrders}
              className={` ${locale == "ar" ? "ml-2" : "mr-2"} flex flex-1 sm:flex-none justify-center text-nowrap items-center gap-2 rounded-xl text-white bg-green-600 shadow-sm py-2 px-4 cursor-pointer text-xs hover:bg-green-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <FileSpreadsheet className="w-3.5 h-3.5" />
              <span>{t('filter_orders.import_google_sheets')}</span>
            </button>
          )}
          {status === "nouveau" && (role === 1 || role === 2) && (
            <button
              onClick={handleConfirmOrders}
              disabled={selectedOrders.length === 0 || loading || confirmingOrders}
              className={`${locale == "ar" ? "ml-2" : "mr-2"} flex flex-1 sm:flex-none justify-center text-nowrap items-center gap-2 rounded-xl text-white bg-[var(--color-primary-500)] shadow-sm py-2 px-4 cursor-pointer text-xs hover:bg-[var(--color-primary-400)] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {confirmingOrders ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t('filter_orders.confirming')}</span>
                </>
              ) : (
                <>
                  <CheckCheck className="w-4 h-4" />
                  <span>{t('filter_orders.confirm', { count: selectedOrders.length })}</span>
                </>
              )}
            </button>
          )}
          {    ((status === "confirme" && role === 1)
            || (status === "confirme" && role === 2 && roles.send_delivery === 1)) &&
          (
            <>
              <button
                onClick={handleSendClick}
                disabled={selectedOrders.length === 0 || sendingOrders}
                className="flex flex-1 sm:flex-none justify-center text-nowrap items-center gap-2 rounded-xl text-white bg-[var(--color-primary-500)] shadow-sm py-2 px-4 cursor-pointer text-xs hover:bg-[var(--color-primary-400)] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingOrders ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>{t('orders.sending_orders')}</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>{t('filter_orders.send', { count: selectedOrders.length })}</span>
                  </>
                )}
              </button>
              
              <button
                onClick={() => setShowLivreurModal(true)}
                disabled={selectedOrders.length === 0}
                className="flex flex-1 sm:flex-none justify-center text-nowrap items-center gap-2 rounded-xl text-white bg-orange-500 shadow-sm py-2 px-4 cursor-pointer text-xs hover:bg-orange-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
                <span>{t('filter_orders.send_to_livreur', { count: selectedOrders.length })}</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Seller Select and Date Range */}
      <div
        onClick={()=>{ 
          if (role === 1 || role === 2) {
            fetchSellers();
          }
         }}
        className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 w-full md:w-auto md:justify-end"
      >
        {(role === 1 || role === 2) && (
          <div className="w-full sm:w-44">
            <Select
              instanceId="seller-select"
              options={options}
              value={selectedSeller}
              isLoading={loadingSellers}
              placeholder={t('forms.placeholders.search_seller')}
              className="text-[11px] rounded-xl border border-gray-200 outline-none w-full"
              onChange={handleSellerChange}
              isClearable={false}
              styles={getSelectStyles()}
            />
          </div>
        )}
        {(role === 1 || role === 2) && (
          <div className="w-full sm:w-44">
            <Select
              onMenuOpen={() => fetchLivreurs()}
              instanceId="livreur-select"
              options={livreurOptions}
              value={selectedLivreur}
              isLoading={loadingLivreurs}
              placeholder={t('forms.placeholders.search_delivery_person')}
              className="text-[11px] rounded-xl border border-gray-200 outline-none w-full"
              onChange={handleLivreurChange}
              isClearable={false}
              styles={getSelectStyles()}
            />
          </div>
        )}

        <div className="w-full sm:w-44">
          <Select
            onMenuOpen={() => fetchProducts()}
            instanceId="product-select"
            options={productOptions}
            value={selectedProduct}
            isLoading={loadingProducts}
            placeholder={t('forms.placeholders.search_products')}
            className="text-[11px] rounded-xl border border-gray-200 outline-none w-full"
            onChange={handleProductChange}
            isClearable={false}
            styles={getSelectStyles()}
          />
        </div>

        {(role === 1 || role === 4) && (
            <button
              onClick={openScanner}
              disabled={loading || sendingOrders}
              className="flex flex-1 sm:flex-none justify-center text-nowrap items-center gap-2 rounded-xl bg-stone-800 dark:bg-stone-900 text-white text-[11px] py-2 px-4 cursor-pointer transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ScanLine className="w-3.5 h-3.5" />
              <span>{t('filter_orders.scan_orders')}</span>
            </button>
          )}

        <div className="w-full sm:w-auto">
          <SelectDate type={true} onDateRangeChange={handleDateRangeChange} />
        </div>

        {/* Refresh Button */}
        <button 
          className="flex justify-center w-full sm:w-auto dark:text-white dark:hover:bg-stone-700 items-center gap-1 border border-gray-200 rounded-xl bg-white border-gray-200 py-2 px-4 cursor-pointer text-xs hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleRefresh}
          disabled={loading || sendingOrders} 
        >
          <RefreshCcw 
            className={`w-4 h-4 text-[var(--color-primary-500)] transition-transform duration-500 ${
              loading ? 'animate-spin' : ''
            }`} 
          />
          <span>{loading ? t('dashboard.on_refrech') : t('dashboard.refrech')}</span>
        </button>
      </div>

      {showCompanyModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm max-h-[90vh] overflow-y-auto">
            <h3 className="text-sm font-semibold mb-4">{t('filter_orders.select_company_title')}</h3>
            
            <div className="space-y-3 mb-6">
              {
              companies.length > 0 ?
              
              companies.map(company => (
                <label
                  key={company.id}
                  className={`flex items-center space-x-3 p-3 border rounded-xl cursor-pointer transition-all ${
                    selectedCompany == company.id 
                      ? 'border-[var(--color-primary-500)] bg-[var(--color-primary-50)] border-2' 
                      : 'border-gray-300 hover:border-[var(--color-primary-200)]'
                  }`}
                >
                  <input
                    type="radio"
                    name="company"
                    value={company.id}
                    checked={selectedCompany === company.id}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                    className="hidden"
                  />
                  
                  <div className={`w-5 h-5 shrink-0 rounded-full border-2 flex items-center justify-center ${
                    selectedCompany == company.id ? 'border-[var(--color-primary-500)]' : 'border-gray-400'
                  }`}>
                    {selectedCompany == company.id && (
                      <div className="w-2.5 h-2.5 bg-[var(--color-primary-500)] rounded-full"></div>
                    )}
                  </div>
                  
                  <img 
                    src={company.logo}
                    width={30}
                    height={30}
                    className="shrink-0"
                    alt={company.name}
                  />
                  
                  <span className="text-gray-800 text-xs font-medium">{company.name}</span>
                </label>
              ))
              :
              <div className="w-full flex justify-center pt-3">
                <Loading />
              </div>
            }
            </div>

            <div className="flex flex-wrap justify-end gap-3">
              <button
                onClick={() => {
                  setShowCompanyModal(false);
                  setSelectedCompany();
                }}
                className="px-4 py-2 text-xs border border-gray-200 rounded-full cursor-pointer text-gray-600 hover:text-gray-800 hover:bg-gray-50 transition-colors"
              >
                {t('buttons.cancel')}
              </button>
              <button
                onClick={handleConfirmSend}
                disabled={!selectedCompany || sendingOrders}
                className="flex text-xs cursor-pointer items-center gap-2 rounded-full text-white bg-[var(--color-primary-500)] shadow-sm py-2 px-4 hover:bg-[var(--color-primary-400)] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingOrders && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('filter_orders.confirm_send')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Google Sheets Modal */}
      {showImportModal && (
        <ImportOrdersGoogleSheets
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          fetchOrders={fetchOrders}
        />
      )}

      {/* QR Scanner Modal */}
      {showScannerModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <ScanLine className="w-4 h-4 text-[var(--color-primary-500)]" />
                {t('filter_orders.scan_orders_title')}
              </h3>
              <button
                onClick={closeScanner}
                className="text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-gray-500 mb-4">
              {t('filter_orders.scan_orders_description')}
            </p>

            <div className="w-full overflow-hidden rounded-xl bg-black">
              <div id="qr-reader" className="w-full"></div>
            </div>

            {cameraError && (
              <div className="mt-3 flex flex-col items-center gap-2">
                <p className="text-xs text-red-500 text-center">{cameraError}</p>
                <button
                  onClick={() => setScannerAttempt((a) => a + 1)}
                  className="px-4 py-2 text-xs text-white bg-indigo-600 rounded-full cursor-pointer hover:bg-indigo-700 transition-colors"
                >
                  {t('filter_orders.scan_retry')}
                </button>
              </div>
            )}

            <div className="flex justify-end mt-4">
              <button
                onClick={closeScanner}
                className="px-4 py-2 text-xs border border-gray-200 rounded-full cursor-pointer text-gray-600 hover:text-gray-800 hover:bg-gray-50 transition-colors"
              >
                {t('buttons.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  
}